const logger = {
  error: (...args: unknown[]) => {
    if (typeof globalThis !== "undefined" && globalThis.console) {
      globalThis.console["error"](...args);
    }
  },
};

/**
 * Theme System - Dark Mode & Custom Themes
 * 
 * Features:
 * - Dark/Light mode toggle
 * - System preference detection
 * - Theme persistence (localStorage)
 * - Custom color themes
 * - Theme preview support
 * - WCAG accessibility compliance
 */

// ============================================================================
// Types
// ============================================================================

export type ThemeMode = 'light' | 'dark' | 'system';

export interface ThemeColors {
  // Primary brand colors
  primary: string;
  primaryHover: string;
  primaryForeground: string;
  
  // Secondary accent
  secondary: string;
  secondaryHover: string;
  secondaryForeground: string;
  
  // Background layers
  background: string;
  backgroundAlt: string;
  surface: string;
  surfaceHover: string;
  surfaceActive: string;
  
  // Borders
  border: string;
  borderHover: string;
  borderFocus: string;
  
  // Text
  text: string;
  textMuted: string;
  textSubtle: string;
  
  // Semantic colors
  success: string;
  successForeground: string;
  warning: string;
  warningForeground: string;
  error: string;
  errorForeground: string;
  info: string;
  infoForeground: string;
  
  // Overlays
  overlay: string;
  backdrop: string;
  
  // Gradients
  gradientStart: string;
  gradientEnd: string;
}

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  colors: {
    light: ThemeColors;
    dark: ThemeColors;
  };
  accentColor: string;
  preview: {
    primary: string;
    secondary: string;
    background: string;
  };
}

export interface UserThemePreferences {
  mode: ThemeMode;
  presetId: string;
  customColors?: Partial<ThemeColors>;
  reducedMotion: boolean;
  highContrast: boolean;
  fontSize: 'small' | 'medium' | 'large';
}

// ============================================================================
// Constants
// ============================================================================

export const THEME_STORAGE_KEY = 'showbold-theme-preferences';
export const THEME_ATTRIBUTE = 'data-theme';
export const MODE_ATTRIBUTE = 'class';

// Default dark theme colors (current app style)
const darkColors: ThemeColors = {
  primary: '#8b5cf6',
  primaryHover: '#7c3aed',
  primaryForeground: '#ffffff',
  
  secondary: '#d946ef',
  secondaryHover: '#c026d3',
  secondaryForeground: '#ffffff',
  
  background: '#0a0a0a',
  backgroundAlt: '#111111',
  surface: '#161616',
  surfaceHover: '#1f1f1f',
  surfaceActive: '#262626',
  
  border: '#262626',
  borderHover: '#333333',
  borderFocus: '#8b5cf6',
  
  text: '#ffffff',
  textMuted: '#a1a1aa',
  textSubtle: '#71717a',
  
  success: '#10b981',
  successForeground: '#ffffff',
  warning: '#f59e0b',
  warningForeground: '#000000',
  error: '#ef4444',
  errorForeground: '#ffffff',
  info: '#3b82f6',
  infoForeground: '#ffffff',
  
  overlay: 'rgba(0, 0, 0, 0.8)',
  backdrop: 'rgba(0, 0, 0, 0.6)',
  
  gradientStart: '#8b5cf6',
  gradientEnd: '#d946ef',
};

// Light theme colors
const lightColors: ThemeColors = {
  primary: '#7c3aed',
  primaryHover: '#6d28d9',
  primaryForeground: '#ffffff',
  
  secondary: '#c026d3',
  secondaryHover: '#a21caf',
  secondaryForeground: '#ffffff',
  
  background: '#ffffff',
  backgroundAlt: '#fafafa',
  surface: '#f4f4f5',
  surfaceHover: '#e4e4e7',
  surfaceActive: '#d4d4d8',
  
  border: '#e4e4e7',
  borderHover: '#d4d4d8',
  borderFocus: '#7c3aed',
  
  text: '#18181b',
  textMuted: '#52525b',
  textSubtle: '#71717a',
  
  success: '#059669',
  successForeground: '#ffffff',
  warning: '#d97706',
  warningForeground: '#000000',
  error: '#dc2626',
  errorForeground: '#ffffff',
  info: '#2563eb',
  infoForeground: '#ffffff',
  
  overlay: 'rgba(255, 255, 255, 0.9)',
  backdrop: 'rgba(0, 0, 0, 0.4)',
  
  gradientStart: '#7c3aed',
  gradientEnd: '#c026d3',
};

// ============================================================================
// Theme Presets
// ============================================================================

export const themePresets: ThemePreset[] = [
  {
    id: 'default',
    name: 'Showbold',
    description: 'Default violet and fuchsia theme',
    colors: { light: lightColors, dark: darkColors },
    accentColor: '#8b5cf6',
    preview: { primary: '#8b5cf6', secondary: '#d946ef', background: '#0a0a0a' },
  },
  {
    id: 'ocean',
    name: 'Ocean',
    description: 'Cool blue and cyan theme',
    colors: {
      light: {
        ...lightColors,
        primary: '#0ea5e9',
        primaryHover: '#0284c7',
        secondary: '#06b6d4',
        secondaryHover: '#0891b2',
        borderFocus: '#0ea5e9',
        gradientStart: '#0ea5e9',
        gradientEnd: '#06b6d4',
      },
      dark: {
        ...darkColors,
        primary: '#0ea5e9',
        primaryHover: '#38bdf8',
        secondary: '#06b6d4',
        secondaryHover: '#22d3ee',
        borderFocus: '#0ea5e9',
        gradientStart: '#0ea5e9',
        gradientEnd: '#06b6d4',
      },
    },
    accentColor: '#0ea5e9',
    preview: { primary: '#0ea5e9', secondary: '#06b6d4', background: '#0a0a0a' },
  },
  {
    id: 'sunset',
    name: 'Sunset',
    description: 'Warm orange and rose theme',
    colors: {
      light: {
        ...lightColors,
        primary: '#f97316',
        primaryHover: '#ea580c',
        secondary: '#f43f5e',
        secondaryHover: '#e11d48',
        borderFocus: '#f97316',
        gradientStart: '#f97316',
        gradientEnd: '#f43f5e',
      },
      dark: {
        ...darkColors,
        primary: '#f97316',
        primaryHover: '#fb923c',
        secondary: '#f43f5e',
        secondaryHover: '#fb7185',
        borderFocus: '#f97316',
        gradientStart: '#f97316',
        gradientEnd: '#f43f5e',
      },
    },
    accentColor: '#f97316',
    preview: { primary: '#f97316', secondary: '#f43f5e', background: '#0a0a0a' },
  },
  {
    id: 'forest',
    name: 'Forest',
    description: 'Natural green and teal theme',
    colors: {
      light: {
        ...lightColors,
        primary: '#10b981',
        primaryHover: '#059669',
        secondary: '#14b8a6',
        secondaryHover: '#0d9488',
        borderFocus: '#10b981',
        gradientStart: '#10b981',
        gradientEnd: '#14b8a6',
      },
      dark: {
        ...darkColors,
        primary: '#10b981',
        primaryHover: '#34d399',
        secondary: '#14b8a6',
        secondaryHover: '#2dd4bf',
        borderFocus: '#10b981',
        gradientStart: '#10b981',
        gradientEnd: '#14b8a6',
      },
    },
    accentColor: '#10b981',
    preview: { primary: '#10b981', secondary: '#14b8a6', background: '#0a0a0a' },
  },
  {
    id: 'midnight',
    name: 'Midnight',
    description: 'Deep indigo and purple theme',
    colors: {
      light: {
        ...lightColors,
        primary: '#6366f1',
        primaryHover: '#4f46e5',
        secondary: '#a855f7',
        secondaryHover: '#9333ea',
        borderFocus: '#6366f1',
        gradientStart: '#6366f1',
        gradientEnd: '#a855f7',
      },
      dark: {
        ...darkColors,
        primary: '#6366f1',
        primaryHover: '#818cf8',
        secondary: '#a855f7',
        secondaryHover: '#c084fc',
        borderFocus: '#6366f1',
        gradientStart: '#6366f1',
        gradientEnd: '#a855f7',
      },
    },
    accentColor: '#6366f1',
    preview: { primary: '#6366f1', secondary: '#a855f7', background: '#0a0a0a' },
  },
  {
    id: 'rose',
    name: 'Rose',
    description: 'Elegant pink and rose theme',
    colors: {
      light: {
        ...lightColors,
        primary: '#ec4899',
        primaryHover: '#db2777',
        secondary: '#f472b6',
        secondaryHover: '#f9a8d4',
        borderFocus: '#ec4899',
        gradientStart: '#ec4899',
        gradientEnd: '#f472b6',
      },
      dark: {
        ...darkColors,
        primary: '#ec4899',
        primaryHover: '#f472b6',
        secondary: '#f472b6',
        secondaryHover: '#f9a8d4',
        borderFocus: '#ec4899',
        gradientStart: '#ec4899',
        gradientEnd: '#f472b6',
      },
    },
    accentColor: '#ec4899',
    preview: { primary: '#ec4899', secondary: '#f472b6', background: '#0a0a0a' },
  },
  {
    id: 'monochrome',
    name: 'Monochrome',
    description: 'Clean black and white theme',
    colors: {
      light: {
        ...lightColors,
        primary: '#18181b',
        primaryHover: '#27272a',
        primaryForeground: '#ffffff',
        secondary: '#52525b',
        secondaryHover: '#71717a',
        secondaryForeground: '#ffffff',
        borderFocus: '#18181b',
        gradientStart: '#18181b',
        gradientEnd: '#3f3f46',
      },
      dark: {
        ...darkColors,
        primary: '#fafafa',
        primaryHover: '#e4e4e7',
        primaryForeground: '#18181b',
        secondary: '#a1a1aa',
        secondaryHover: '#d4d4d8',
        secondaryForeground: '#18181b',
        borderFocus: '#fafafa',
        gradientStart: '#fafafa',
        gradientEnd: '#a1a1aa',
      },
    },
    accentColor: '#52525b',
    preview: { primary: '#18181b', secondary: '#52525b', background: '#ffffff' },
  },
  {
    id: 'amber',
    name: 'Amber',
    description: 'Warm amber and gold theme',
    colors: {
      light: {
        ...lightColors,
        primary: '#f59e0b',
        primaryHover: '#d97706',
        secondary: '#fbbf24',
        secondaryHover: '#f59e0b',
        borderFocus: '#f59e0b',
        gradientStart: '#f59e0b',
        gradientEnd: '#fbbf24',
      },
      dark: {
        ...darkColors,
        primary: '#f59e0b',
        primaryHover: '#fbbf24',
        secondary: '#fbbf24',
        secondaryHover: '#fcd34d',
        borderFocus: '#f59e0b',
        gradientStart: '#f59e0b',
        gradientEnd: '#fbbf24',
      },
    },
    accentColor: '#f59e0b',
    preview: { primary: '#f59e0b', secondary: '#fbbf24', background: '#0a0a0a' },
  },
];

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the system's preferred color scheme
 */
export function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Check if user prefers high contrast
 */
export function prefersHighContrast(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-contrast: more)').matches;
}

/**
 * Get stored theme preferences from localStorage
 */
export function getStoredPreferences(): UserThemePreferences | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    logger.error('Failed to parse theme preferences:', error instanceof Error ? error : new Error(String(error)));
  }
  return null;
}

/**
 * Store theme preferences in localStorage
 */
export function storePreferences(preferences: UserThemePreferences): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(preferences));
  } catch (error) {
    logger.error('Failed to store theme preferences:', error instanceof Error ? error : new Error(String(error)));
  }
}

/**
 * Get default theme preferences
 */
export function getDefaultPreferences(): UserThemePreferences {
  return {
    mode: 'system',
    presetId: 'default',
    reducedMotion: prefersReducedMotion(),
    highContrast: prefersHighContrast(),
    fontSize: 'medium',
  };
}

/**
 * Get a theme preset by ID
 */
export function getPreset(presetId: string): ThemePreset {
  return themePresets.find((p) => p.id === presetId) || themePresets[0];
}

/**
 * Get resolved theme mode (handles 'system' preference)
 */
export function getResolvedMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return getSystemTheme();
  }
  return mode;
}

/**
 * Get theme colors for current mode and preset
 */
export function getThemeColors(
  presetId: string,
  mode: 'light' | 'dark',
  customColors?: Partial<ThemeColors>
): ThemeColors {
  const preset = getPreset(presetId);
  const baseColors = preset.colors[mode];
  return customColors ? { ...baseColors, ...customColors } : baseColors;
}

/**
 * Apply theme to document
 */
export function applyTheme(preferences: UserThemePreferences): void {
  if (typeof document === 'undefined') return;

  const resolvedMode = getResolvedMode(preferences.mode);
  const colors = getThemeColors(preferences.presetId, resolvedMode, preferences.customColors);

  // Apply mode class
  document.documentElement.classList.remove('light', 'dark');
  document.documentElement.classList.add(resolvedMode);

  // Apply theme preset attribute
  document.documentElement.setAttribute(THEME_ATTRIBUTE, preferences.presetId);

  // Apply CSS custom properties
  const root = document.documentElement;
  Object.entries(colors).forEach(([key, value]) => {
    const cssVar = `--theme-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVar, value);
  });

  // Apply accessibility preferences
  if (preferences.reducedMotion) {
    root.classList.add('reduce-motion');
  } else {
    root.classList.remove('reduce-motion');
  }

  if (preferences.highContrast) {
    root.classList.add('high-contrast');
  } else {
    root.classList.remove('high-contrast');
  }

  // Apply font size
  root.classList.remove('text-size-small', 'text-size-medium', 'text-size-large');
  root.classList.add(`text-size-${preferences.fontSize}`);
}

/**
 * Calculate contrast ratio between two colors
 * Used for accessibility checking
 */
export function getContrastRatio(foreground: string, background: string): number {
  const getLuminance = (hex: string): number => {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0;
    
    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((v) => {
      const normalized = v / 255;
      return normalized <= 0.03928
        ? normalized / 12.92
        : Math.pow((normalized + 0.055) / 1.055, 2.4);
    });
    
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const l1 = getLuminance(foreground);
  const l2 = getLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Convert hex color to RGB
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Convert RGB to hex color
 */
export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => {
    const hex = Math.round(Math.max(0, Math.min(255, x))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

/**
 * Check if a color combination meets WCAG AA standards
 */
export function meetsWcagAA(foreground: string, background: string, isLargeText = false): boolean {
  const ratio = getContrastRatio(foreground, background);
  return isLargeText ? ratio >= 3 : ratio >= 4.5;
}

/**
 * Check if a color combination meets WCAG AAA standards
 */
export function meetsWcagAAA(foreground: string, background: string, isLargeText = false): boolean {
  const ratio = getContrastRatio(foreground, background);
  return isLargeText ? ratio >= 4.5 : ratio >= 7;
}

/**
 * Generate a lighter shade of a color
 */
export function lighten(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  return rgbToHex(
    rgb.r + (255 - rgb.r) * amount,
    rgb.g + (255 - rgb.g) * amount,
    rgb.b + (255 - rgb.b) * amount
  );
}

/**
 * Generate a darker shade of a color
 */
export function darken(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  return rgbToHex(
    rgb.r * (1 - amount),
    rgb.g * (1 - amount),
    rgb.b * (1 - amount)
  );
}

/**
 * Initialize theme on page load
 * Call this in your app's entry point
 */
export function initializeTheme(): UserThemePreferences {
  const stored = getStoredPreferences();
  const preferences = stored || getDefaultPreferences();
  
  applyTheme(preferences);
  
  // Listen for system theme changes
  if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', () => {
      if (preferences.mode === 'system') {
        applyTheme(preferences);
      }
    });
  }
  
  return preferences;
}
